import { memo, ReactNode } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import { locale as dailyQuotaChartLocale } from 'src/pages/Operation/OperationFIDC/OperationFIDCAdministrator/DailyQuotaCharts/utils';
import { CHART_COLORS as yearbooks2026ChartColors } from 'src/pages/Yearbooks/2026/constants';

import { formatChartDateLabel } from '../dateUtils';

const CHART_COLORS = yearbooks2026ChartColors;
const CDI_COLOR = '#6D6E71';
const DEFAULT_PERCENT_FORMATTER = (value: number | null | undefined) =>
  value == null ? 'Sem dado' : `${value.toFixed(2)}%`;

export const chartFrameSx = (theme: Theme) => ({
  p: { xs: 1.5, md: 2.5 },
  borderRadius: 1,
  borderColor: theme.palette.divider,
  backgroundColor: theme.palette.background.paper,
  boxShadow: 'none',
  '.apexcharts-menu-item.exportCSV': { display: 'none' },
});

export const sectionSx = (theme: Theme) => ({
  p: { xs: 2, md: 3 },
  borderRadius: 1,
  borderColor: theme.palette.divider,
  backgroundColor: theme.palette.background.paper,
});

export const titleIconSx = {
  fontSize: 15,
  color: 'text.disabled',
  cursor: 'help',
  '&:hover': { color: 'text.secondary' },
  transition: 'color 0.15s',
};

type ChartLabelFormatterContext = {
  dataPointIndex?: number;
  w?: {
    globals?: {
      categoryLabels?: string[];
      labels?: string[];
    };
  };
};

const resolveChartLabel = (
  value: unknown,
  context?: ChartLabelFormatterContext,
) => {
  const labelIndex = context?.dataPointIndex;

  if (
    typeof labelIndex === 'number' &&
    labelIndex >= 0 &&
    context?.w?.globals?.categoryLabels?.[labelIndex]
  ) {
    return context.w.globals.categoryLabels[labelIndex];
  }

  if (
    typeof labelIndex === 'number' &&
    labelIndex >= 0 &&
    context?.w?.globals?.labels?.[labelIndex]
  ) {
    return context.w.globals.labels[labelIndex];
  }

  return String(value ?? '');
};

const formatResolvedChartDate = (
  value: unknown,
  context?: ChartLabelFormatterContext,
) => formatChartDateLabel(resolveChartLabel(value, context));

const formatChartAxisLabel = (
  value: string,
  _timestamp?: number,
  opts?: ChartLabelFormatterContext,
) => formatResolvedChartDate(value, opts);

const formatChartXAxisTooltipDate = (
  value: string,
  opts?: ChartLabelFormatterContext,
) => formatResolvedChartDate(value, opts);

const formatChartTooltipDate = (
  value: number,
  opts?: ChartLabelFormatterContext,
) => formatResolvedChartDate(value, opts);

const buildChartColors = (seriesLength: number, benchmarkSeriesCount = 0) => {
  const fundSeriesCount = Math.max(seriesLength - benchmarkSeriesCount, 0);

  return [
    ...Array.from(
      { length: fundSeriesCount },
      (_, index) => CHART_COLORS[index % CHART_COLORS.length],
    ),
    ...Array.from({ length: benchmarkSeriesCount }, () => CDI_COLOR),
  ];
};

export type PeriodOption = {
  value: string;
  label: string;
};

export function PeriodSelector({
  selectorId,
  selectedPeriod,
  periodOptions,
  onChange,
}: {
  selectorId: string;
  selectedPeriod: string;
  periodOptions: PeriodOption[];
  onChange: (period: string) => void;
}) {
  const theme = useTheme();

  return (
    <ButtonGroup
      variant="contained"
      color="inherit"
      size="small"
      aria-label="Selecionar período do gráfico"
      sx={{
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        boxShadow: theme.shadows[1],
        '& .MuiButtonGroup-grouped': {
          minWidth: 'auto',
          px: 1.5,
          py: 0.75,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1.5,
          textTransform: 'none',
          border: 'none',
          boxShadow: 'none',
        },
      }}>
      {periodOptions.map((option) => (
        <Button
          key={option.value}
          size="small"
          aria-pressed={option.value === selectedPeriod}
          data-testid={`period-selector-${selectorId}-${option.value}`}
          sx={{
            ...(option.value === selectedPeriod
              ? {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': { backgroundColor: theme.palette.primary.dark },
                }
              : {
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.secondary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }),
          }}
          onClick={() => onChange(option.value)}>
          {option.label}
        </Button>
      ))}
    </ButtonGroup>
  );
}

export function ChartTitle({
  title,
  titleHelp,
  subtitle,
}: {
  title: string;
  titleHelp?: ReactNode;
  subtitle?: ReactNode;
}) {
  const theme = useTheme();

  return (
    <Stack gap={0.25} mb={2}>
      <Box
        display="flex"
        alignItems="center"
        gap={0.75}
        sx={{
          '&::after': {
            content: '""',
            height: 1,
            bgcolor: theme.palette.divider,
            flex: 1,
            mt: 0.35,
          },
        }}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ color: 'text.primary', whiteSpace: 'nowrap' }}>
          {title}
        </Typography>
        {titleHelp && (
          <Tooltip title={titleHelp} arrow placement="top" enterDelay={200}>
            <IconButton
              size="small"
              aria-label={`Informações sobre ${title}`}
              sx={{ p: 0.25, flexShrink: 0 }}>
              <InfoOutlinedIcon sx={titleIconSx} fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {subtitle && (
        <Box display="flex" justifyContent="flex-end">
          {subtitle}
        </Box>
      )}
    </Stack>
  );
}

export const LineChart = memo(function LineChart({
  title,
  titleHelp,
  subtitle,
  series,
  noDataText,
  footnote,
  yAxisFormatter = DEFAULT_PERCENT_FORMATTER,
  tooltipFormatter = DEFAULT_PERCENT_FORMATTER,
  benchmarkSeriesCount = 0,
  selectedPeriod,
  periodOptions,
  onPeriodChange,
  selectorId,
}: {
  title: string;
  titleHelp?: ReactNode;
  subtitle?: ReactNode;
  series: ApexAxisChartSeries;
  noDataText?: string;
  footnote?: ReactNode;
  yAxisFormatter?: (value: number | null | undefined) => string;
  tooltipFormatter?: (value: number | null | undefined) => string;
  benchmarkSeriesCount?: number;
  selectedPeriod: string;
  periodOptions: PeriodOption[];
  onPeriodChange: (period: string) => void;
  selectorId: string;
}) {
  const theme = useTheme();
  const colors = buildChartColors(series.length, benchmarkSeriesCount);
  const benchmarkStartIndex = series.length - benchmarkSeriesCount;

  return (
    <Paper variant="outlined" sx={chartFrameSx(theme)}>
      <ChartTitle title={title} titleHelp={titleHelp} subtitle={subtitle} />
      <Box display="flex" justifyContent="flex-end" mb={1.5}>
        <PeriodSelector
          selectorId={selectorId}
          selectedPeriod={selectedPeriod}
          periodOptions={periodOptions}
          onChange={onPeriodChange}
        />
      </Box>
      <ReactApexChart
        type="line"
        height={420}
        series={series}
        options={{
          chart: {
            type: 'line',
            defaultLocale: 'pt-BR',
            locales: [dailyQuotaChartLocale],
            toolbar: {
              show: true,
              autoSelected: 'zoom',
              tools: {
                download: true,
                selection: false,
                zoom: true,
                zoomin: true,
                zoomout: true,
                pan: true,
                reset: true,
              },
            },
            zoom: { enabled: true, type: 'xy', autoScaleYaxis: false },
            fontFamily: theme.typography.fontFamily,
          },
          dataLabels: { enabled: false },
          xaxis: {
            type: 'category',
            labels: {
              formatter: formatChartAxisLabel,
              style: {
                colors: theme.palette.text.secondary,
                fontFamily: theme.typography.fontFamily,
              },
            },
            tooltip: {
              enabled: true,
              formatter: formatChartXAxisTooltipDate,
            },
            axisBorder: { color: theme.palette.divider },
            axisTicks: { color: theme.palette.divider },
          },
          yaxis: {
            labels: {
              formatter: yAxisFormatter,
              style: {
                colors: [theme.palette.text.secondary],
                fontFamily: theme.typography.fontFamily,
              },
            },
          },
          stroke: {
            curve: 'smooth',
            width: series.map((_, index) =>
              benchmarkSeriesCount > 0 && index >= benchmarkStartIndex ? 3 : 4,
            ),
            dashArray: series.map((_, index) =>
              benchmarkSeriesCount > 0 && index >= benchmarkStartIndex ? 4 : 0,
            ),
          },
          markers: {
            size: 0,
            hover: { size: 6 },
          },
          colors,
          states: {
            hover: {
              filter: { type: 'darken', value: 0.05 },
            },
          },
          grid: {
            borderColor: theme.palette.divider,
            strokeDashArray: 3,
          },
          tooltip: {
            shared: true,
            intersect: false,
            x: { formatter: formatChartTooltipDate },
            y: { formatter: tooltipFormatter },
            style: { fontFamily: theme.typography.fontFamily },
          },
          legend: {
            show: true,
            showForSingleSeries: true,
            position: 'top',
            horizontalAlign: 'center',
            floating: false,
            offsetY: 20,
            height: 50,
            itemMargin: {
              horizontal: 10,
              vertical: 5,
            },
            labels: { colors: [theme.palette.text.primary] },
            fontFamily: theme.typography.fontFamily,
          },
          noData: {
            text: noDataText ?? 'Sem dados para a competência selecionada',
            style: {
              color: theme.palette.text.disabled,
              fontSize: '14px',
              fontFamily: theme.typography.fontFamily,
            },
            offsetY: -20,
          },
        }}
      />
      {footnote ? (
        <Typography
          variant="caption"
          color="text.disabled"
          mt={1}
          display="block">
          {footnote}
        </Typography>
      ) : null}
    </Paper>
  );
});
